import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiTrendingUp, FiAlertCircle, FiEye } from 'react-icons/fi';
import { API_URL } from '../../../services/api';

interface TopCompany {
  company_id: string;
  company_name: string;
  view_count: number;
  period: string;
}

interface TopCompaniesResponse {
  success: boolean;
  data: TopCompany[];
  message?: string;
}

const TopCompaniesWidget: React.FC = () => {
  const [companies, setCompanies] = useState<TopCompany[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTopCompanies();
  }, []);

  const fetchTopCompanies = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('ไม่พบ access token กรุณาเข้าสู่ระบบใหม่');
      }

      console.log('Fetching top companies from API...');

      
      const response = await axios.get<TopCompaniesResponse>(
        `${API_URL}/api/admin/dashboard/top-companies`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log('Top companies response:', response.data);

      // Handle both wrapped and direct array responses
      if (Array.isArray(response.data)) {
        // Direct array response
        setCompanies(response.data);
        console.log('Top companies loaded successfully:', response.data);
      } else if (response.data.success && response.data.data) {
        // Wrapped response
        setCompanies(response.data.data);
        console.log('Top companies loaded successfully:', response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch top companies');
      }
    } catch (err: any) {
      console.error('Error fetching top companies:', err);
      setError(err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">บริษัทยอดนิยม</h3>
          <FiTrendingUp className="h-5 w-5 text-blue-600" />
        </div>
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">บริษัทยอดนิยม</h3>
          <FiTrendingUp className="h-5 w-5 text-blue-600" />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <FiAlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">เกิดข้อผิดพลาด</h4>
              <p className="text-sm text-red-700 mb-3">{error}</p>
              <button
                onClick={fetchTopCompanies}
                className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
              >
                ลองอีกครั้ง
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (companies.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">บริษัทยอดนิยม</h3>
          <FiTrendingUp className="h-5 w-5 text-blue-600" />
        </div>
        <div className="text-center py-8">
          <FiEye className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">ยังไม่มีข้อมูลการเข้าชม</p>
        </div>
      </div>
    );
  }

  // Success State - Display Companies
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">บริษัทยอดนิยม</h3>
        <FiTrendingUp className="h-5 w-5 text-blue-600" />
      </div>

      <div className="space-y-1">
        {companies.map((company, index) => (
          <div
            key={company.company_id}
            className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 rounded-md px-2 transition-colors"
          >
            <div className="flex items-center flex-1 min-w-0">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm mr-3 flex-shrink-0">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {company.company_name}
                </p>
                <p className="text-xs text-gray-500">
                  {company.period === 'last_30_days' ? '30 วันที่ผ่านมา' : company.period}
                </p>
              </div>
            </div>
            <div className="flex items-center ml-3 flex-shrink-0">
              <FiEye className="h-4 w-4 text-gray-400 mr-1" />
              <span className="text-sm font-semibold text-gray-900">
                {company.view_count.toLocaleString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopCompaniesWidget;
